import type { ChartRow } from '@deepdive-ui';
import {
  chartPaletteSize,
  chartSeriesTokens,
  type ChartSeries,
  type ChartSeriesToken,
} from '@deepdive-ui';
import { seriesToken, X_KEY } from './aggregate';
import { dimensionIndex, labelOf, type DimensionKey } from './dimensions';
import {
  AGE_BANDS,
  COMMUNITY_LABELS,
  COMMUNITY_VALUES,
  DIVISION_LABELS,
  DIVISION_VALUES,
  GENDER_LABELS,
  GENDER_VALUES,
  HASHTAG_LABELS,
  HASHTAG_VALUES,
  INDUSTRY_LABELS,
  INDUSTRY_VALUES,
  INFLUENCER_LABELS,
  INFLUENCER_VALUES,
  KEYWORD_LABELS,
  KEYWORD_VALUES,
  ORIGIN_VALUES,
  OUTLET_LABELS,
  OUTLET_VALUES,
  SAFETY_BANDS,
  SAFETY_BAND_LABELS,
  VOICE_LABELS,
  VOICE_VALUES,
  type MentionRow,
} from './generator';
import { metrics, type MetricId } from './metrics';

/**
 * Rows in, a **categorical** chart out.
 *
 * `aggregate` puts time on the x axis — always. That is right for a trend and
 * wrong for the three questions this file answers: what is each topic made of,
 * how does engagement break down by type, and how does the seeded/organic split
 * stand. None of them has a time axis at all.
 *
 * The plan predicted this: *"if one needs new plumbing, the abstraction in
 * Phases 3–4 was wrong and gets fixed there, not patched locally."* So this is a
 * sibling of `aggregate` rather than a flag on it — the two answer different
 * shapes of question and share the parts that genuinely are shared (`X_KEY`,
 * `ChartSeries`, fixture-ordered colour).
 *
 * The three properties `aggregate` guarantees hold here too: `series` is a
 * result rather than an input, `total` reduces the same array the categories
 * were built from, and colour is indexed off fixture order so a filter cannot
 * recolour the page.
 */

/**
 * A **roster** — an ordered, closed set of values with no filter chip behind it.
 *
 * `origin`, `outlet` and the four Community categories are not `DimensionKey`s,
 * because dimensions are the fields the chips control and there is no Origin,
 * Outlets, Communities, Voices, Divisions or Audience chip. They are still
 * ordered closed sets that need stable colours and display names, so they are
 * declared once here rather than special-cased at each of the four call sites
 * below — which is what the `origin`/`outlet` pair had grown into.
 *
 * `read` is the row accessor rather than an index, so the nullable ones type
 * check: `outlet`, `community` and `voice` are all fields a row can genuinely
 * lack, and `null` is what makes such a row skippable instead of a bar labelled
 * `null`.
 */
type Roster = {
  values: readonly string[];
  labels: Readonly<Record<string, string>>;
  read: (row: MentionRow) => string | null;
};

const ORIGIN_LABELS: Readonly<Record<string, string>> = {
  organic: 'Organic',
  seeded: 'Seeded',
};

/**
 * `ageBand` is its own label set — the bands are already written the way they
 * should read (`25-34`), so the map is the identity and exists only so every
 * roster answers the same three questions.
 */
const AGE_BAND_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  AGE_BANDS.map((band) => [band, band]),
);

const ROSTERS = {
  origin: { values: ORIGIN_VALUES, labels: ORIGIN_LABELS, read: (r) => r.origin },
  outlet: { values: OUTLET_VALUES, labels: OUTLET_LABELS, read: (r) => r.outlet },
  community: { values: COMMUNITY_VALUES, labels: COMMUNITY_LABELS, read: (r) => r.community },
  voice: { values: VOICE_VALUES, labels: VOICE_LABELS, read: (r) => r.voice },
  division: { values: DIVISION_VALUES, labels: DIVISION_LABELS, read: (r) => r.division },
  ageBand: { values: AGE_BANDS, labels: AGE_BAND_LABELS, read: (r) => r.ageBand },
  gender: { values: GENDER_VALUES, labels: GENDER_LABELS, read: (r) => r.gender },
  /*
   * The two Trends vocabularies. `keyword` is non-null on every row and
   * `hashtag` is not — see the fields' own notes. Both are rosters rather than
   * dimensions for the reason every roster here is one: there is no Keywords
   * chip and no Hashtags chip, and adding either to `dimensionKeys` would make
   * `assertRowsMatchFixtures` demand a fixture that does not exist.
   */
  keyword: { values: KEYWORD_VALUES, labels: KEYWORD_LABELS, read: (r) => r.keyword },
  hashtag: { values: HASHTAG_VALUES, labels: HASHTAG_LABELS, read: (r) => r.hashtag },
  /*
   * The three Influencers rosters. All three are nullable together — a row
   * either has a creator behind it or it has none of the three — so a module
   * that forgets to scope to creator rows first gets a chart that silently
   * ignores three quarters of what it was handed, rather than one labelled
   * `null`. `influencerRows` in `data/influencers.ts` is that scoping step.
   */
  influencer: { values: INFLUENCER_VALUES, labels: INFLUENCER_LABELS, read: (r) => r.influencer },
  industry: { values: INDUSTRY_VALUES, labels: INDUSTRY_LABELS, read: (r) => r.industry },
  safetyBand: { values: SAFETY_BANDS, labels: SAFETY_BAND_LABELS, read: (r) => r.safetyBand },
} as const satisfies Record<string, Roster>;

/**
 * `safetyBand` is the second category in the app with a *meaning* to be
 * faithful to, after sentiment — and it is the same meaning, so it takes the
 * same three tones rather than the next three off the ramp. `safe` must not be
 * able to come out red because it happened to sit third in a roster.
 */
const SAFETY_BAND_TOKENS: Readonly<Record<string, ChartSeriesToken>> = {
  safe: 'chart-4',
  'needs-review': 'chart-5',
  risky: 'chart-6',
};

/** A row field that can sit on a categorical axis. */
export type CategoryKey = DimensionKey | keyof typeof ROSTERS;

/** The rosters, narrowed — `key in ROSTERS` does not narrow a union on its own. */
function rosterFor(key: CategoryKey): Roster | undefined {
  return (ROSTERS as Record<string, Roster>)[key];
}

function indexOf(key: CategoryKey, value: string): number {
  const roster = rosterFor(key);
  if (roster) {
    const i = roster.values.indexOf(value);
    return i < 0 ? 0 : i;
  }
  return dimensionIndex[key as DimensionKey][value] ?? 0;
}

function labelFor(key: CategoryKey, value: string): string {
  const roster = rosterFor(key);
  if (roster) return roster.labels[value] ?? value;
  return labelOf(key as DimensionKey, value);
}

/**
 * The colour token for one value of a category.
 *
 * Delegates to `seriesToken` for a real dimension, so a categorical chart and a
 * time-bucketed one colour the same value identically — and so sentiment gets
 * its `component/progress` tones here too rather than only in `aggregate`.
 * A roster has no chip and so no fixture order to borrow; it takes the ramp,
 * indexed off its own declared order.
 */
export function categoryToken(key: CategoryKey, value: string): ChartSeriesToken {
  if (key === 'safetyBand') {
    const byMeaning = SAFETY_BAND_TOKENS[value];
    if (byMeaning) return byMeaning;
  }
  if (rosterFor(key)) {
    return chartSeriesTokens[indexOf(key, value) % chartPaletteSize];
  }
  return seriesToken(key as DimensionKey, value);
}

/** A row's value for a category, or `null` when it has none. */
function valueAt(row: MentionRow, key: CategoryKey): string | null {
  const roster = rosterFor(key);
  if (roster) return roster.read(row);
  return row[key as DimensionKey];
}

export type PivotResult = {
  rows: ChartRow[];
  xKey: typeof X_KEY;
  series: ChartSeries[];
  /** The metric over **all** the rows — the figure a KPI shows. */
  total: number;
  /**
   * The corpus rows behind one point — every row in that category, across every
   * series. What a readout's `View details` opens; see `AggregateResult.rowsAt`,
   * which this mirrors.
   *
   * Indexed against `rows`, so it follows the sort and the `limit`: the reader
   * clicks the third bar they can see, not the third category the corpus holds.
   */
  rowsAt: (index: number) => MentionRow[];
};

export type PivotSpec = {
  metric: MetricId;
  /** The category on the x axis. */
  xBy: CategoryKey;
  /** Split into one series per value of this category. Omit for a single series. */
  splitBy?: CategoryKey;
  /**
   * `fixture` keeps the chips' own order — right when the axis is a scale the
   * reader already knows. `value` sorts by the metric, descending, which is
   * what a ranked chart wants.
   */
  sort?: 'fixture' | 'value';
  /**
   * With `sort: 'value'`, rank by **one series** rather than by the category's
   * total — the key of the series to rank on.
   *
   * "Which topics have the most angry mentions" is a question about the
   * negative series, not about topic size, and the two give different orders. It
   * is deliberately the series' *value* and not its share: ranking by negative
   * **rate** would let a topic with three mentions, all negative, outrank one
   * carrying two thousand. Volume is what makes the ranking actionable.
   */
  sortBy?: string;
  /** Keep only the first N categories after sorting. */
  limit?: number;
};

/**
 * One category per x position, optionally split into series.
 *
 * Categories are those **present in the rows**, not every value the chips
 * offer: a filtered-out topic should leave the chart rather than stand there as
 * a labelled zero. Series follow the same rule, for the same reason.
 */
export function pivot(rows: readonly MentionRow[], spec: PivotSpec): PivotResult {
  const metric = metrics[spec.metric];
  const { xBy, splitBy } = spec;

  const groups = new Map<string, Map<string, MentionRow[]>>();
  for (const row of rows) {
    /*
     * A row with no value for the category is skipped, not bucketed under a
     * label. Only `outlet` can be null, and a bar reading `null` for "nobody
     * published this" would be worse than its absence. `total` below still
     * reduces the rows as given, so a caller that hands in unpublished rows
     * gets a figure larger than its own bars sum to — which is why the Media
     * module filters to published rows before calling in.
     */
    const x = valueAt(row, xBy);
    if (x === null) continue;
    const seriesKey = splitBy ? valueAt(row, splitBy) : spec.metric;
    if (seriesKey === null) continue;

    let bySeries = groups.get(x);
    if (!bySeries) {
      bySeries = new Map();
      groups.set(x, bySeries);
    }
    const list = bySeries.get(seriesKey);
    if (list) list.push(row);
    else bySeries.set(seriesKey, [row]);
  }

  const series: ChartSeries[] = splitBy
    ? seriesForSplit(rows, splitBy)
    : [{ key: spec.metric, label: metric.label, color: chartSeriesTokens[0] }];

  let categories = [...groups.keys()];

  if (spec.sort === 'value') {
    /*
     * Ranked by the metric over the whole category by default, or over a single
     * series when `sortBy` names one. Both are volumes, never shares: ranking on
     * a rate would let a category with three mentions, all of them negative,
     * outrank one carrying two thousand.
     */
    const weight = new Map(
      categories.map((c) => {
        const bySeries = groups.get(c)!;
        const subject = spec.sortBy
          ? (bySeries.get(spec.sortBy) ?? [])
          : [...bySeries.values()].flat();
        return [c, metric.reduce(subject)] as const;
      }),
    );
    categories.sort((a, b) => (weight.get(b) ?? 0) - (weight.get(a) ?? 0));
  } else {
    categories.sort((a, b) => indexOf(xBy, a) - indexOf(xBy, b));
  }

  if (spec.limit !== undefined) categories = categories.slice(0, spec.limit);

  const chartRows: ChartRow[] = categories.map((category) => {
    const bySeries = groups.get(category)!;
    const row: ChartRow = { [X_KEY]: labelFor(xBy, category) };
    for (const s of series) {
      /* `0`, never `null` — the same rule `aggregate` follows, for the same reason. */
      const group = bySeries.get(s.key);
      row[s.key] = group ? metric.reduce(group) : 0;
    }
    return row;
  });

  return {
    rows: chartRows,
    xKey: X_KEY,
    series,
    total: metric.reduce(rows),
    rowsAt: (index) => {
      const category = categories[index];
      if (category === undefined) return [];
      return [...(groups.get(category)?.values() ?? [])].flat();
    },
  };
}

export type MetricPivotSpec = {
  /**
   * The metrics on the x axis, in plot order — one bar or group per metric.
   * This is the shape `aggregate` and `pivot` both lack: the *thing being
   * measured* is the category, rather than a field on the row.
   */
  metrics: MetricId[];
  /** Split each metric into one series per value of this category. */
  splitBy?: CategoryKey;
};

/**
 * One **metric** per x position — "engagement by type", where the four types are
 * four metrics rather than four values of a field.
 *
 * Modelling them as metrics is what keeps the arithmetic honest: a mention
 * generates shares *and* comments *and* reactions *and* likes, so a row cannot
 * carry a single `engagementType`. Making it a dimension would have forced each
 * mention into one bucket and quietly changed what the chart claims.
 *
 * `total` sums every metric listed, which for the engagement types is exactly
 * the `engagement` figure — they are guaranteed to add back to it.
 */
export function pivotMetrics(rows: readonly MentionRow[], spec: MetricPivotSpec): PivotResult {
  const { splitBy } = spec;

  const series: ChartSeries[] = splitBy
    ? seriesForSplit(rows, splitBy)
    : [{ key: 'value', label: 'Total', color: chartSeriesTokens[0] }];

  /* Bucket once, then reduce per metric — rather than re-scanning per cell. */
  const bySeries = new Map<string, MentionRow[]>();
  for (const row of rows) {
    const key = splitBy ? valueAt(row, splitBy) : 'value';
    if (key === null) continue;
    const list = bySeries.get(key);
    if (list) list.push(row);
    else bySeries.set(key, [row]);
  }

  const chartRows: ChartRow[] = spec.metrics.map((id) => {
    const row: ChartRow = { [X_KEY]: metrics[id].label };
    for (const s of series) {
      const group = bySeries.get(s.key);
      row[s.key] = group ? metrics[id].reduce(group) : 0;
    }
    return row;
  });

  const total = spec.metrics.reduce((sum, id) => sum + metrics[id].reduce(rows), 0);

  return {
    rows: chartRows,
    xKey: X_KEY,
    series,
    total,
    /*
     * Every row, whichever bar was clicked — because that is the truth here and
     * a narrower answer would be a lie. A metric pivot's x positions are not
     * subsets of the corpus: `Comments` and `Shares` are both reduced over the
     * *same* rows, so there is no "the conversations behind Comments" to return.
     *
     * A card wiring `onViewDetails` to one of these should therefore think
     * twice: the modal will open the same list from every bar, which is honest
     * but rarely what a reader clicking one bar expects.
     */
    rowsAt: () => [...rows],
  };
}

/**
 * One series per value present in the rows, ordered and coloured by fixture
 * order. The categorical twin of `aggregate`'s own `seriesForSplit`.
 */
function seriesForSplit(rows: readonly MentionRow[], splitBy: CategoryKey): ChartSeries[] {
  const present = new Set<string>();
  for (const row of rows) {
    const value = valueAt(row, splitBy);
    if (value !== null) present.add(value);
  }

  return [...present]
    .sort((a, b) => indexOf(splitBy, a) - indexOf(splitBy, b))
    .map((value) => ({
      key: value,
      label: labelFor(splitBy, value),
      color: categoryToken(splitBy, value),
    }));
}
